import type {MouseEventHandler, ReactElement} from 'react';
import {useState} from 'react';
import type {IconWithTooltipProps} from './icon-with-tooltip.types';
import {TooltipPlacement} from './icon-with-tooltip.types';
import {deriveArrowPositionFromPlacement, getTooltipOverlayClassName} from './icon-with-tooltip-helper';
import styles from './icon-with-tooltip.module.scss';
import {Icon} from '@Components/icon-v2';
import {isTouchDevice} from '@Utils/browser.util';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';

export function IconWithTooltip({arrowPosition, tooltipText, tooltipContent, hideArrow = false, placement = TooltipPlacement.TOP, tooltipClassName: tooltipBubbleClassName = '', togglesOnTap = false, ...props}: IconWithTooltipProps): ReactElement {
  const [show, setShow] = useState(false);
  const resolvedArrowPosition = arrowPosition ?? deriveArrowPositionFromPlacement(placement);
  const tooltipClassName = `${getTooltipOverlayClassName(hideArrow, resolvedArrowPosition)} ${styles.tooltip} ${tooltipBubbleClassName}`;

  /**
   * A touch device never hovers, so an opted-in icon drops the hover trigger and lets the tap toggle the bubble
   * instead. Anywhere a pointer can hover, the bubble keeps appearing on hover and being dismissed by a click.
   */
  const togglesBubbleOnTap = togglesOnTap && isTouchDevice();

  const handleClick: MouseEventHandler = (event): void => {
    if (togglesBubbleOnTap && props.onClick === undefined) {
      setShow((wasShown: boolean) => {
        return !wasShown;
      });
      return;
    }
    setShow(false);
    props.onClick?.(event);
  };

  return (
    <OverlayTrigger
      show={show}
      onToggle={(nextShow: boolean): void => {
        setShow(nextShow);
      }}
      trigger={togglesBubbleOnTap ? ['focus'] : ['hover', 'focus']}
      overlay={<Tooltip className={tooltipClassName}>{tooltipContent ?? tooltipText}</Tooltip>}
      delay={{show: 0, hide: props.toolTipHideDelay ?? 0}}
      placement={placement}
      offset={props.offset ? [props.offset.x, props.offset.y] : undefined}
    >
      <Icon {...props} className={props.className} onClick={handleClick} />
    </OverlayTrigger>
  );
}

